import { useCallback, useEffect, useState } from 'react';
import type { TextAreaElementRef } from './TextArea.types';
interface UseAutoGrowOptions {
  autoGrow: boolean;
  minRows: number;
  maxRows: number;
}

export function useAutoGrow(
  textareaRef: TextAreaElementRef,
  value: string,
  { autoGrow, minRows, maxRows }: UseAutoGrowOptions
) {
  const [currentHeight, setCurrentHeight] = useState<number | undefined>();

  const updateHeight = useCallback(() => {
    if (!autoGrow || !textareaRef.current) return;

    const textarea = textareaRef.current;
    const computedStyle = window.getComputedStyle(textarea);
    const lineHeight = parseFloat(computedStyle.lineHeight) || 20;
    const paddingTop = parseFloat(computedStyle.paddingTop) || 0;
    const paddingBottom = parseFloat(computedStyle.paddingBottom) || 0;
    const borderTop = parseFloat(computedStyle.borderTopWidth) || 0;
    const borderBottom = parseFloat(computedStyle.borderBottomWidth) || 0;

    const singleLineHeight =
      lineHeight + paddingTop + paddingBottom + borderTop + borderBottom;
    const minHeight = Math.max(
      singleLineHeight,
      lineHeight * minRows +
        paddingTop +
        paddingBottom +
        borderTop +
        borderBottom
    );
    const maxHeight =
      lineHeight * maxRows +
      paddingTop +
      paddingBottom +
      borderTop +
      borderBottom;

    textarea.style.height = 'auto';
    const scrollHeight = textarea.scrollHeight;
    const newHeight = Math.min(Math.max(scrollHeight, minHeight), maxHeight);

    textarea.style.height = `${newHeight}px`;
    setCurrentHeight(newHeight);
    textarea.style.overflowY = scrollHeight > maxHeight ? 'auto' : 'hidden';
  }, [autoGrow, minRows, maxRows, textareaRef]);

  useEffect(() => {
    updateHeight();
  }, [value, updateHeight]);

  return { currentHeight, updateHeight };
}
